if (!customElements.get('mundo-led-vertical')) {
  customElements.define('mundo-led-vertical', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      this.slides = [...this.querySelectorAll('.mlv__slide')];
      this.dots = [...this.querySelectorAll('.mlv__dot')];
      this.index = -1;
      this.editorSelected = false;
      this.visible = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.dialog = this.querySelector('.mlv__dialog');
      this.galleries = this.slides.map(slide => ({
        photos: [...slide.querySelectorAll('.mlv__photo')],
        dots: [...slide.querySelectorAll('.mlv__photo-dot')],
        index: 0
      }));
      this.galleries.forEach((gallery, slideIndex) => {
        gallery.dots.forEach((dot, photoIndex) => dot.addEventListener('click', () => this.showPhoto(slideIndex, photoIndex), opts));
        this.slides[slideIndex].querySelector('.mlv__carousel')?.addEventListener('keydown', e => {
          if (!['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
          e.preventDefault();
          e.stopPropagation();
          this.showPhoto(slideIndex, gallery.index + (e.key === 'ArrowRight' ? 1 : -1));
        }, opts);
      });
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.show(i, true), opts));
      this.querySelector('.mlv__prev')?.addEventListener('click', () => this.show(this.index - 1, true), opts);
      this.querySelector('.mlv__next')?.addEventListener('click', () => this.show(this.index + 1, true), opts);
      this.addEventListener('keydown', e => {
        if (this.dialog.open || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
        e.preventDefault();
        this.show(this.index + (e.key === 'ArrowRight' ? 1 : -1), true);
      }, opts);
      this.querySelectorAll('[data-enlarge]').forEach(button => button.addEventListener('click', () => {
        const image = button.closest('.mlv__slide').querySelector('.mlv__photo[aria-hidden="false"] .mlv__image');
        if (!image) return;
        const enlarged = image.cloneNode();
        enlarged.removeAttribute('srcset');
        enlarged.removeAttribute('sizes');
        enlarged.loading = 'eager';
        const crop = button.closest('.mlv__slide').querySelector('.mlv__photo[aria-hidden="false"]').dataset.crop;
        if (crop === 'upper' || crop === 'lower') {
          const frame = document.createElement('div');
          frame.className = 'mlv__enlarged-crop mlv__enlarged-crop--' + crop;
          frame.appendChild(enlarged);
          this.dialog.querySelector('.mlv__dialog-image').replaceChildren(frame);
        } else {
          this.dialog.querySelector('.mlv__dialog-image').replaceChildren(enlarged);
        }
        this.dialog.showModal();
      }, opts));
      this.querySelector('.mlv__close').addEventListener('click', () => this.dialog.close(), opts);
      document.addEventListener('shopify:block:select', e => {
        const i = this.slides.findIndex(slide => slide.dataset.blockId === e.detail.blockId);
        if (i >= 0) { this.editorSelected = true; this.show(i); }
      }, opts);
      document.addEventListener('shopify:block:deselect', e => {
        if (this.slides.some(slide => slide.dataset.blockId === e.detail.blockId)) this.editorSelected = false;
      }, opts);
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }, { threshold: 0.15 });
      this.observer.observe(this);
      this.show(0);
      this.timer = setInterval(() => {
        const focused = this.slides[this.index]?.contains(document.activeElement);
        if (this.dataset.autoplay !== 'true' || this.motion.matches || this.editorSelected || !this.visible || document.hidden || this.dialog.open || focused) {
          this.resetDeadlines();
          return;
        }
        if (Date.now() >= this.nextBlockAt && this.slides.length > 1) {
          this.show(this.index + 1);
          return;
        }
        const gallery = this.galleries[this.index];
        if (Date.now() >= this.nextPhotoAt && gallery?.photos.length > 1) this.showPhoto(this.index, gallery.index + 1);
      }, 250);
    }
    show(i, announce = false) {
      if (!this.slides.length) return;
      const previous = this.index;
      const next = (i + this.slides.length) % this.slides.length;
      if (previous === next) return;
      const direction = i < previous ? -1 : 1;
      const duration = this.motion.matches ? 0 : 850;
      this.slides.forEach(slide => {
        slide.getAnimations().forEach(animation => animation.cancel());
        slide.style.visibility = 'hidden';
        slide.style.transform = 'translateX(-110%)';
        slide.setAttribute('aria-hidden', 'true');
        slide.inert = true;
      });
      const incoming = this.slides[next];
      incoming.style.transform = 'translateX(0)';
      incoming.style.visibility = 'visible';
      incoming.setAttribute('aria-hidden', 'false');
      incoming.inert = false;
      if (previous >= 0 && duration) {
        const outgoing = this.slides[previous];
        outgoing.style.transform = 'translateX(' + direction * 110 + '%)';
        outgoing.style.visibility = 'visible';
        const timing = { duration, easing: 'cubic-bezier(.2,.7,.2,1)' };
        const exit = outgoing.animate([{transform:'translateX(0)'},{transform:'translateX(' + direction * 110 + '%)'}], timing);
        incoming.animate([{transform:'translateX(' + -direction * 110 + '%)'},{transform:'translateX(0)'}], timing);
        exit.onfinish = () => { if (outgoing.getAttribute('aria-hidden') === 'true') outgoing.style.visibility = 'hidden'; };
      }
      this.index = next;
      this.showPhoto(next, 0, true);
      this.resetDeadlines();
      this.dots.forEach((dot, n) => { dot.classList.toggle('is-active', n === next); dot.setAttribute('aria-pressed', String(n === next)); });
      if (announce) this.querySelector('.mlv__status').textContent = (next + 1) + ' / ' + this.slides.length;
    }
    resetDeadlines() {
      this.nextBlockAt = Date.now() + (Number(this.dataset.delay) || 12000);
      this.nextPhotoAt = Date.now() + (Number(this.dataset.photoDelay) || 3000);
    }
    showPhoto(slideIndex, i, immediate = false) {
      const gallery = this.galleries[slideIndex];
      if (!gallery?.photos.length) return;
      const previous = gallery.index;
      const next = (i + gallery.photos.length) % gallery.photos.length;
      const direction = i < previous ? -1 : 1;
      const duration = immediate || this.motion.matches || previous === next ? 0 : 650;
      gallery.photos.forEach(photo => {
        photo.getAnimations().forEach(animation => animation.cancel());
        photo.style.transform = 'translateX(-100%)';
        photo.style.visibility = 'hidden';
        photo.setAttribute('aria-hidden', 'true');
      });
      const incoming = gallery.photos[next];
      incoming.style.transform = 'translateX(0)';
      incoming.style.visibility = 'visible';
      incoming.setAttribute('aria-hidden', 'false');
      if (duration) {
        const outgoing = gallery.photos[previous];
        outgoing.style.transform = 'translateX(' + direction * 100 + '%)';
        outgoing.style.visibility = 'visible';
        const timing = {duration, easing:'cubic-bezier(.2,.7,.2,1)'};
        const exit = outgoing.animate([{transform:'translateX(0)'},{transform:'translateX(' + direction * 100 + '%)'}],timing);
        incoming.animate([{transform:'translateX(' + -direction * 100 + '%)'},{transform:'translateX(0)'}],timing);
        exit.onfinish = () => { if (outgoing.getAttribute('aria-hidden') === 'true') outgoing.style.visibility = 'hidden'; };
      }
      gallery.index = next;
      gallery.dots.forEach((dot,n) => {dot.classList.toggle('is-active', n === next);dot.setAttribute('aria-pressed',String(n === next));});
      this.nextPhotoAt = Date.now() + (Number(this.dataset.photoDelay) || 3000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.slides?.forEach(slide => slide.getAnimations().forEach(animation => animation.cancel()));
      this.galleries?.forEach(gallery => gallery.photos.forEach(photo => photo.getAnimations().forEach(animation => animation.cancel())));
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
